import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Col, Container, FormCheck, FormControl, Row} from 'react-bootstrap';
import {useSearchParams} from 'react-router-dom';

import {service as ActivityFeedFilterService} from "../../../Services/ActivityFeedFilterService";
import {service as ActivityTypeService} from "../../../Services/ActivityTypeService";

import {useDebouncedEffect} from "../../../Hooks/EffectHooks";
import { useMakePageRequest } from "../../../Hooks/RequestHooks";

import {useClassNames} from "../../../Hooks/StyleHooks";

import {ActivityFeed} from "../../Common/ActivityFeed/ActivityFeed";
import {ActivityTypeFilter} from '../../Common/ActivityFeed/ActivityTypeFilter';
import {
    ActivityFeedSubscriptionDropDown
} from '../../Common/ActivityFeed/ActivityFeedSubscriptionDropDown';
import {service as ContextNodeActivityService} from "../../../Services/ContextNodeActivityService";
import {service as UserService} from "../../../Services/UserService";
import {useSelector} from "react-redux";
import {selectDeepestBreadcrumb} from "../../../Redux/Selectors/contextSelectors";
import {selectCurrentUser} from "../../../Redux/Selectors/userSelectors";
import {useDefaultErrorHandler} from "../../../Hooks/ErrorHooks";
import {ArrayUtils} from "../../../Utils/ArrayUtils";
import DateUtils from "../../../../../monitoring/src/Utils/DateUtils";

export const ContextNodeActivityPage = props => {
    
    const DONT_LOAD_YET = 0;

    const classNames = useClassNames('sb-activity-page');
    const makePageRequest = useMakePageRequest();
    
    const [isLoading, setIsLoading] = useState(false);
    const [events, setEvents] = useState(null);
    const [eventTypes, setEventTypes] = useState([]);
    const [eventTypeFilter, setEventTypeFilter] = useState(null);
    const [lastActivityId, setLastActivityId] = useState(DONT_LOAD_YET);
    const [feedEpoch, setFeedEpoch] = useState(0);
    const [visibleActivityCount, setVisibleActivityCount] = useState(0);
    const [hasMoreActivity, setHasMoreActivity] = useState(true);

    // const projectId = props.match.params.projId;
    const {contextNodeId} = useSelector(state => state.contextState.context);
    const contextNode = useSelector(selectDeepestBreadcrumb);
    const currentUser = useSelector(selectCurrentUser);
    const defaultErrorHandler = useDefaultErrorHandler();

    // per-user persisted preference; the JWT payload carries it (default on)
    const [showSystemActivities, setShowSystemActivities] = useState(currentUser?.showSystemActivities ?? true);

    const [searchParams, setSearchParams] = useSearchParams();

    // Day-granularity created bounds (?start=YYYY-MM-DD&end=YYYY-MM-DD, both ends
    // inclusive) carried by daily-summary email links. Anything that isn't a real
    // calendar date is treated as absent (the API revalidates independently), so
    // mangled URLs degrade to the normal unbounded feed.
    const dateRange = useMemo(() => {
        const parseDateParam = name => {
            const value = searchParams.get(name);
            return (value && /^\d{4}-\d{2}-\d{2}$/.test(value) && DateUtils.FormatDate(value) !== null)
                ? value
                : null;
        };
        const start = parseDateParam('start');
        const end = parseDateParam('end');
        return (start || end) ? {start, end} : null;
    }, [searchParams]);

    const dateRangeLabel = useMemo(() => {
        if(!dateRange) {
            return null;
        }
        const label = value => DateUtils.FormatDate(value, 'MMM d, yyyy');
        if(dateRange.start && dateRange.end) {
            return `from ${label(dateRange.start)} to ${label(dateRange.end)}`;
        }
        return dateRange.start ? `since ${label(dateRange.start)}` : `through ${label(dateRange.end)}`;
    }, [dateRange]);

    // The pickers hold local edit state and commit to the URL on a short
    // debounce: a native date input fires change per keystroke while a segment
    // is being typed (a year of "2" is already the valid date 0002), and
    // committing each one would reset and refetch the feed on a transient
    // window. Driving the inputs straight from the URL would also let any
    // re-render during the debounce snap an input back mid-edit.
    const [dateEdit, setDateEdit] = useState({start: dateRange?.start ?? '', end: dateRange?.end ?? ''});

    // external range changes (deep link, Show all, back/forward, node
    // navigation) re-populate the inputs; because the commit below restarts
    // whenever the inputs change, this also collapses any in-flight edit's
    // commit into a no-op instead of letting it write the pre-navigation
    // params over the new URL
    useEffect(() => {
        setDateEdit({start: dateRange?.start ?? '', end: dateRange?.end ?? ''});
    }, [dateRange?.start, dateRange?.end]);

    // the settled inputs commit to the same URL params the email deep links
    // carry, so a picked range is shareable and a deep-linked range
    // pre-populates the pickers; the range effect below does the re-fetch.
    // Committing values the URL already holds changes nothing: the params
    // stay equal, so no effect downstream re-fires.
    useDebouncedEffect(() => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            for(const [name, value] of Object.entries(dateEdit)) {
                if(value) {
                    next.set(name, value);
                } else {
                    next.delete(name);
                }
            }
            return next;
        }, {replace: true});
    }, [dateEdit.start, dateEdit.end], 350);

    const bottomOfActivityFeed = useRef(null);

    // it looks like this was never used?
    const scrollToBottom = () => {
        bottomOfActivityFeed.scrollIntoView({ behavior: "smooth" });
    }

    const mergeActivities = (existingContextNodeActivities, potentiallyNewContextNodeActivities) => {
        existingContextNodeActivities = existingContextNodeActivities ?? [];

        // an empty page means the server has nothing below the cursor; without
        // this a filtered feed whose FIRST page is empty would auto-load forever
        if (potentiallyNewContextNodeActivities.length === 0) {
            setHasMoreActivity(false);
            return existingContextNodeActivities;
        }

        if (existingContextNodeActivities.length === 0) {
            return potentiallyNewContextNodeActivities;
        }

        const certifiedNewContextNodeActivities = potentiallyNewContextNodeActivities.filter(potentiallyNewContextNodeActivity =>
            existingContextNodeActivities.find(x =>
                x.contextNodeActivityId === potentiallyNewContextNodeActivity.contextNodeActivityId
            ) === undefined
        );

        if(certifiedNewContextNodeActivities.length === 0) {
            setHasMoreActivity(false);
            return existingContextNodeActivities;
        }

        return existingContextNodeActivities.concat(certifiedNewContextNodeActivities);
    };

    useEffect(() => {
        setEvents(null);
        setLastActivityId(DONT_LOAD_YET);
        makePageRequest(
            () => Promise.all([
                ActivityTypeService.Get(contextNodeId),
            ]),
            ([eventTypes]) => {
                setEventTypes(eventTypes.result);
                setHasMoreActivity(true);
                setLastActivityId(null);
                setVisibleActivityCount(0);
            }
        );

    }, [contextNodeId]);

    // distinct event-type ids of the active filter, passed to the server so each
    // page contains type-matching rows (instead of the client crawling the whole
    // feed one empty page at a time); jsonKey refinement stays client-side
    const filterActivityTypeIds = useMemo(() =>
        eventTypeFilter
            ? [...new Set(eventTypeFilter.ActivityFeedFilterConditions.map(x => Number(x.ActivityType.activityTypeId)))]
            : null,
    [eventTypeFilter]);

    useEffect(() => {
        if(lastActivityId === DONT_LOAD_YET) { return; }
        setIsLoading(true);
        makePageRequest(
            () => Promise.all([
                ContextNodeActivityService.Get(contextNodeId, filterActivityTypeIds, showSystemActivities, lastActivityId, dateRange?.start, dateRange?.end),
            ]),
            ([eventsAjax]) => {
                setEvents(mergeActivities(events, eventsAjax.result));
            },
            () => {
                setIsLoading(false);
            }
        );
    }, [lastActivityId, feedEpoch]);

    // reset and reload the feed from the top (fetch effect keys on feedEpoch so
    // this works even when lastActivityId is already null)
    const resetFeed = () => {
        setEvents(null);
        setHasMoreActivity(true);
        setVisibleActivityCount(0);
        setLastActivityId(null);
        setFeedEpoch(x => x + 1);
    };

    // a changed range restarts the feed from the top; the mount run is skipped
    // because the contextNodeId effect already schedules the first load
    const dateRangeMounted = useRef(false);
    useEffect(() => {
        if(!dateRangeMounted.current) {
            dateRangeMounted.current = true;
            return;
        }
        resetFeed();
    }, [dateRange?.start, dateRange?.end]);

    const updateDateEdit = (name, value) => {
        setDateEdit(prev => ({...prev, [name]: value}));
    };

    // dropping the params (rather than navigating) keeps any future params
    // intact and lets the range effect above do the single re-fetch
    const onShowAllClick = () => {
        const next = new URLSearchParams(searchParams);
        next.delete('start');
        next.delete('end');
        setSearchParams(next, {replace: true});
    };

    const updateEventTypeFilter = (value) => {
        setEventTypeFilter(ActivityFeedFilterService.CachedValueById(value)?.result);
        resetFeed();
    };

    const updateShowSystemActivities = (value) => {
        const previous = showSystemActivities;
        setShowSystemActivities(value);
        // persist FIRST, re-fetch only after it lands: every response re-embeds a
        // JWT built from ITS OWN request's claims, so a re-fetch racing the POST
        // could write the stale claim back to localStorage and undo the persist
        void makePageRequest(
            () => Promise.all([
                UserService.UpdatePreferences({showSystemActivities: value}),
            ]),
            () => resetFeed(),
            (error) => {
                setShowSystemActivities(previous);
                defaultErrorHandler(error);
            }
        );
    };

    const onClearFiltersClick = () => {
        setEventTypeFilter(null);
        if(dateRange) {
            // the range effect performs the reset once the params update
            onShowAllClick();
        } else {
            // an uncommitted edit may still be sitting in the inputs
            setDateEdit({start: '', end: ''});
            resetFeed();
        }
    }

    const onLoadMoreClick = () => {
        if(!hasMoreActivity) {
            return;
        }
        
        if(events?.length) {
            const lastId = Math.min(...(events.map(x => x.contextNodeActivityId)));
            setLastActivityId(lastId);
        }
    };
    
    const filteredEvents = useMemo(() => {
        if (events == null) {
            return events;
        }
        
        let result = [...events];
        
        if (eventTypeFilter) {
            // Group conditions by activityTypeId. Within a group, every jsonKey/jsonValue predicate
            // must be satisfied (AND); across groups, any matching group suffices (OR). Empty jsonKey
            // means the condition only asserts on activityTypeId. jsonKey may use dotted paths to
            // reach into nested Activity.data (e.g. "newData.conservationActionTypeId"), matching the
            // JSON_EXTRACT(data, '$.<jsonKey>') behavior of the server-side recipient SQL in
            // ActivityService::getSubscribedActivitiesForUser — see the long comment there for the
            // rationale and a sketch of how to introduce explicit condition groups if needed.
            const resolvePath = (root, path) =>
                path.split('.').reduce((acc, segment) => (acc == null ? acc : acc[segment]), root);
            result = result.filter((x) => {
                const matchingConditions = eventTypeFilter.ActivityFeedFilterConditions.filter(y =>
                    Number(y.ActivityType.activityTypeId) === Number(x.Activity.activityTypeId)
                );
                if (matchingConditions.length === 0) {
                    return false;
                }
                return matchingConditions.every(y =>
                    y.jsonKey === "" || String(resolvePath(x.Activity.data, y.jsonKey)) === String(y.jsonValue)
                );
            });
        }
        
        return result;
    },[events, eventTypeFilter])
    
    const filteredEventsGroupedByDate = useMemo(() => {
        if (filteredEvents == null) {
            return filteredEvents;
        }
        
        return ArrayUtils.GroupBy(
            filteredEvents,
            x => DateUtils.FormatToLocalDate(x.created, 'MMMM yyyy'),
            (a, b) => DateUtils.ParseUTCDateTime(a.groupOrigin.created) < DateUtils.ParseUTCDateTime(b.groupOrigin.created)
        );
    }, [filteredEvents]);
    
    useEffect(() => {
        
        const newVisibleActivityCount = filteredEvents?.length ?? 0;
        
        console.log('check if we should load events', hasMoreActivity, newVisibleActivityCount);
        
        // this will also load at the start when newVisibleActivityCount === 0
        if (hasMoreActivity &&
            (newVisibleActivityCount === 0 || newVisibleActivityCount === visibleActivityCount)
        ) {
            // this means we loaded more data, but there were
            // no new activities matching the filter
            // in this case we should continue loading
            setTimeout(() => {
                onLoadMoreClick();
            }, 100);
        } else {
            // this means we loaded more data or there is no more data to load
            setIsLoading(false);
        }
        
        // either way we should update the visible activity count
        setVisibleActivityCount(newVisibleActivityCount);
    }, [events?.length, eventTypeFilter, hasMoreActivity]);

    return <Container className={classNames}>
        <Row>
            <Col xs={12} className="sb-sticky-header">
                <h2 className="atomic-sectionheader">
                    {contextNode?.title ?? 'Activity Feed'}
                    <div className="sb-activity-feed-controls float-right">
                        <Row>
                            <Col xs="auto" className="d-flex align-items-center">
                                <FormCheck
                                    id="show-system-activities"
                                    type="checkbox"
                                    label="Show System Activities"
                                    checked={showSystemActivities}
                                    onChange={event => updateShowSystemActivities(event.target.checked)}
                                />
                            </Col>
                            <Col xs="auto" className="d-flex align-items-center sb-activity-feed-date-filter">
                                <FormControl
                                    type="date"
                                    value={dateEdit.start}
                                    max={dateEdit.end || undefined}
                                    onChange={event => updateDateEdit('start', event.target.value)}
                                    aria-label="Start date"
                                    title="Start date"
                                />
                                <span className="mx-1">–</span>
                                <FormControl
                                    type="date"
                                    value={dateEdit.end}
                                    min={dateEdit.start || undefined}
                                    onChange={event => updateDateEdit('end', event.target.value)}
                                    aria-label="End date"
                                    title="End date"
                                />
                            </Col>
                            <Col>
                                <ActivityTypeFilter
                                    UpdateValue={updateEventTypeFilter}
                                    Value={eventTypeFilter}
                                    ActivityTypes={eventTypes}
                                />
                            </Col>
                            <Col>
                                {
                                    contextNode ?
                                        <ActivityFeedSubscriptionDropDown
                                            ContextNodeId={contextNodeId}
                                        />:
                                        null
                                }
                            </Col>
                        </Row>
                    </div>
                </h2>
            </Col>
            {
                dateRange ?
                    <Col xs={12}>
                        <div className="sb-activity-feed-date-range-notice">
                            Showing activity {dateRangeLabel} — <a className="link-primary" onClick={onShowAllClick}>Show all</a>
                        </div>
                    </Col> :
                    null
            }
            <Col xs={12}>
                <ActivityFeed
                    events={filteredEventsGroupedByDate}
                    hasFilters={eventTypeFilter !== null || dateRange !== null}
                    hasMoreEvents={hasMoreActivity}
                    isLoading={isLoading}
                    onClearFiltersClick={onClearFiltersClick}
                    onLoadMoreClick={onLoadMoreClick}
                />
            </Col>
        </Row>
        <div style={{ float:"left", clear: "both" }}
             ref={bottomOfActivityFeed}
        />
    </Container>;
};
